import React, { useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import AuthCard, { FingerprintMark, PencilMark, PlayMark } from '../auth-card';
import TwoFactorPrompt from '../two-factor-prompt';
import useAuth from '~/modules/hooks/use-auth';
import AuthService from '~/modules/services/auth-service';
import PasskeyService from '~/modules/services/passkey-service';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Button } from '~/components/ui/button';
import SpotlightBackground from './spotlight-background';
import { getInitialTheme } from '../theme-switcher';

/**
 * What an address has to look like when the field cannot say -- something, an `@`, something. The
 * browser's own `type="email"` check is asked first and is the one that counts; this is for the
 * odd environment that has no constraint validation at all.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+$/;

const STEP_IDENTIFIER = 'identifier';
const STEP_PASSKEY = 'passkey';
const STEP_PASSWORD = 'password';
const STEP_TWO_FACTOR = 'two-factor';
const STEP_LINK_SENT = 'link-sent';

/**
 * How long the resend button stays shut. A second link invalidates the first, so somebody
 * pressing it twice would break the link they were waiting for.
 */
const RESEND_SECONDS = 60;

// The quieter of the two actions on a screen -- a way through that is never the one being led
// with, so it reads as text rather than a competing button.
const ALTERNATIVE_BUTTON_CLASS = 'w-full h-auto py-2 font-normal text-muted-foreground hover:text-foreground';

/**
 * The front door, and the only one.
 *
 * An address, and nothing else: what follows is a passkey for whoever has one and a one-time
 * link to the mailbox for everybody else, both of which are stronger than a password and
 * neither of which can be given away over the phone.
 *
 * The identity providers are not offered here. Their buttons and services are still in the
 * tree, unrendered, and the API's SSO endpoints still work -- so bringing them back is putting
 * the row below the button back. Every reason to prefer a passkey is in `docs/sessions.md`.
 *
 * No second factor is asked for here. A passkey is already both factors, and the link flow asks
 * for an authenticator code on its own page -- see `pages/sign-in`.
 */
function AuthModal() {
	let [email, setEmail] = useState('');
	// Which screen the sign-in is on. The flow is one question at a time, and which question
	// comes second depends on what the API says the address has.
	let [step, setStep] = useState(STEP_IDENTIFIER);
	let [checking, setChecking] = useState(false);
	let [stepError, setStepError] = useState(null);
	let [resendIn, setResendIn] = useState(0);
	// Whether this address may *only* sign in with its passkey, which the API decides -- it is
	// administrators, while the flag for it is on. It changes one thing here: the link is not
	// offered as a way round the passkey, because that is exactly what it would be.
	let [passkeyRequired, setPasskeyRequired] = useState(false);
	// Whether this address has a passkey *that can be used here*: the account holds one, this
	// browser can do WebAuthn, and the deployment can run a ceremony. The password screen needs
	// it so it can offer the better credential rather than stranding somebody on the one being
	// retired.
	let [passkeyAvailable, setPasskeyAvailable] = useState(false);
	// The account may only sign in with its passkey, and there is no way to run a ceremony from
	// here -- the deployment has no relying party, or the browser cannot do WebAuthn. Nothing
	// else is on offer for such an account, so the screen says so rather than presenting a
	// button whose every press would fail.
	let [passkeyUnavailable, setPasskeyUnavailable] = useState(false);
	let [password, setPassword] = useState('');
	// What a password sign-in was answered with when the account owes a second factor. The
	// challenge token is not a session; it is spent on the next screen along with a code.
	let [challenge, setChallenge] = useState(null);
	let [verifying, setVerifying] = useState(false);
	// Which flow put us on the link-sent screen. The emailed *second factor* is a different
	// message from the passwordless sign-in link, and there is no endpoint to send another of it.
	let [linkKind, setLinkKind] = useState('signin');

	const {
		isLoggedIn,
		lastAttemptedAt,
		passkeyLogin,
		isPasskeyLoggingIn,
		login,
		isLoggingIn,
		ssoLoginError,
		clearLoginErrors
	} = useAuth();

	// One lock for every way in. The mutation flag only goes true once a ceremony has *returned*
	// something, so without this a second sign-in could be started while the system prompt is
	// still open, and whichever session mutation answered last would overwrite the stored one.
	let [ssoBusy, setSsoBusy] = useState(false);

	const signingIn = ssoBusy || isPasskeyLoggingIn || isLoggingIn || verifying;

	// And one for every action on every screen. `checking` (the method lookup and the link
	// request) and `signingIn` (the passkey, the password, the code) used to gate different
	// buttons, so on the passkey and password screens a link could be requested while a sign-in
	// was in flight, or the reverse: two answers in the air, whichever landed last set the step,
	// and a live link nobody still wanted went out. Every button and every handler asks this.
	const busy = checking || signingIn;

	// The address field, for the browser's own validity check. See `handleIdentifier`.
	const emailRef = useRef(null);

	// Read once rather than subscribed to: `SpotlightBackground` only wants this to notice a
	// change (it dips its opacity for a beat when it does), and this screen offers no way to
	// change theme itself -- so a stable snapshot of whatever is already active is enough, and
	// re-reading `localStorage` on every render would not be.
	const [spotlightThemeKey] = useState(getInitialTheme);

	// Counts the resend button down rather than leaving it live: a second link invalidates the
	// first, so a person pressing it twice in frustration would break the link they already had.
	useEffect(() => {
		if( resendIn <= 0 ) {
			return undefined;
		}

		const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);

		return () => clearTimeout(timer);
	}, [resendIn]);

	const handleEmailChange = (e) => {
		setEmail(e.target.value);
	};

	/**
	 * Chrome fills the field without always firing an input event, which would leave the button
	 * disabled over an address that is plainly there. The animation is the only reliable signal.
	 */
	const handleAutoFill = (e) => {
		if( e.animationName === 'onAutoFillStart' ) {
			setEmail(e.target.value);
		}
	};

	const shouldBeOpen = () => {
		return !!(!isLoggedIn && lastAttemptedAt);
	};

	const sendLink = async(identifier) => {
		if( busy ) {
			return;
		}

		setChecking(true);
		setStepError(null);

		try {
			await AuthService.requestSignInLink({ identifier });
			setLinkKind('signin');
			setStep(STEP_LINK_SENT);
			setResendIn(RESEND_SECONDS);
		}
		catch(e) {
			setStepError(e?.message || 'Could not send a link right now');
		}
		finally {
			setChecking(false);
		}
	};

	/**
	 * First screen: find out what this address can use.
	 *
	 * A passkey if it has one, and otherwise a link to the mailbox — which is also the answer for
	 * an address with no account at all, because the API deliberately cannot tell us apart.
	 */
	const handleIdentifier = async(e) => {
		e.preventDefault();

		const identifier = email.trim();

		if( !identifier || busy ) {
			return;
		}

		// Every button here is `type="button"` and Enter is handled by hand, so the form's own
		// submission -- and with it the `type="email"` check -- never runs. Asked explicitly
		// instead: `foo` used to reach the API and land on "Check your email", which is a lie
		// about an address that is not one. `reportValidity()` shows the browser's message where
		// there is one; the error line says the same for anything that cannot.
		const field = emailRef.current;
		const looksLikeAddress = field ? field.checkValidity() : EMAIL_SHAPE.test(identifier);

		if( !looksLikeAddress ) {
			if( field && typeof field.reportValidity === 'function' ) {
				field.reportValidity();
			}

			setStepError('Enter a valid email address');

			return;
		}

		setChecking(true);
		setStepError(null);
		setPasskeyUnavailable(false);

		try {
			// Two questions asked together: what this address can use, and whether a passkey
			// ceremony can be run from here at all. The second is the browser (WebAuthn) *and*
			// the deployment (a relying party, reported by the boot `system-check`); without
			// either, `/session/passkey-options` answers 503 every time, and a passkey screen
			// would be a button whose every press fails.
			const [methods, canCeremony] = await Promise.all([
				AuthService.signInMethods({ identifier }),
				PasskeyService.isSupported()
					? PasskeyService.isConfigured().catch(() => false)
					: Promise.resolve(false)
			]);

			const { passkey, passkeyRequired: required, password: allowsPassword } = methods;
			const passkeyUsable = !!passkey && !!canCeremony;

			setPasskeyRequired(required);
			setPasskeyAvailable(passkeyUsable);

			// The password leads wherever it is still allowed, because the only reason it is on
			// is that somebody deliberately turned it back on. The API already answers false
			// here for an account held to its passkey, so this cannot undercut the requirement.
			if( allowsPassword ) {
				setStep(STEP_PASSWORD);
				setChecking(false);

				return;
			}

			if( passkeyUsable ) {
				setStep(STEP_PASSKEY);
				setChecking(false);
				return;
			}

			// An account held to its passkey, from a place that cannot run a ceremony. The link
			// is refused for it by the API, and a passkey button would fail every time, so the
			// honest answer is to say so.
			if( passkey && required ) {
				setPasskeyUnavailable(true);
				setStep(STEP_PASSKEY);
				setChecking(false);
				return;
			}

			// Everybody else gets the link: the account has no passkey, or this browser or
			// deployment cannot use the one it has.
			setChecking(false);
			await sendLink(identifier);
		}
		catch(err) {
			setStepError(err?.message || 'Could not continue right now');
			setChecking(false);
		}
	};

	/**
	 * Enter is the Continue button.
	 *
	 * Spelled out rather than left to the form: every button here is `type="button"`, as the
	 * house ESLint rule requires, so the form has no submit button at all. Chrome and Firefox
	 * still submit one that has a single field, and Safari does nothing — which made Enter work
	 * or not work depending on the browser.
	 */
	const handleEnter = (e) => {
		if( e.key !== 'Enter' ) {
			return;
		}

		// `handleIdentifier` calls `preventDefault()`, which also suppresses the implicit
		// submission the browsers that do it would otherwise perform -- so this runs once.
		handleIdentifier(e);
	};

	/**
	 * Second screen: run the ceremony.
	 *
	 * A dismissed prompt is a decision rather than a failure, so it leaves the screen alone. A
	 * refusal from the API is worth saying, and either way the link remains a way through.
	 */
	const handlePasskey = async(e) => {
		e.preventDefault();

		if( busy ) {
			return;
		}

		setSsoBusy(true);
		setStepError(null);

		try {
			// With the address, so the browser offers this account's passkeys and the API
			// refuses an assertion from any other. Without it, picking the wrong entry in the
			// system picker signs you in as somebody else -- which is exactly what a screen
			// showing one email address should never do.
			const assertion = await PasskeyService.authenticate({ identifier: email.trim() });

			await passkeyLogin(assertion);
		}
		catch(err) {
			if( err?.name !== 'NotAllowedError' && err?.name !== 'AbortError' ) {
				setStepError(err?.message || 'That passkey could not be used');
			}
		}
		finally {
			setSsoBusy(false);
		}
	};

	/**
	 * Third screen, only where the password has been switched back on.
	 *
	 * Goes through the same `login` mutation the platform has always used, which is why nothing
	 * about the second factor or the session needed rebuilding for this -- the credential was
	 * never removed from the API, only refused.
	 */
	const handlePassword = async(e) => {
		e.preventDefault();

		if( !password || busy ) {
			return;
		}

		setStepError(null);

		try {
			const result = await login({ username: email.trim(), password });

			// A correct password is not always a session. An account with `enforce2fa`, an
			// enrolled authenticator, or the emailed-link preference is answered with a challenge
			// instead, and `login` returns it rather than throwing -- so without this the screen
			// simply sat there, having been told nothing was wrong.
			if( result?.twofactor ) {
				setChallenge(result);

				// The emailed factor needs no field: the link in the mailbox is the whole of it.
				if( result.method === 'totp' ) {
					setStep(STEP_TWO_FACTOR);
				}
				else {
					setLinkKind('twofactor');
					setStep(STEP_LINK_SENT);
				}
			}
		}
		catch(err) {
			setStepError(err?.message || 'Could not sign you in');
		}
	};

	/**
	 * Spend the challenge from a password sign-in, with a code or a recovery code.
	 *
	 * The same two steps `pages/sign-in` takes after an emailed link: exchange the challenge for
	 * a session token, then sign in with it.
	 */
	const handleSecondFactor = async(proof) => {
		if( busy ) {
			return;
		}

		setVerifying(true);
		setStepError(null);

		try {
			// `AuthService` directly rather than `useAuth().twoFactorLogin`, which is react-query's
			// `mutate`: it answers `undefined` and never rejects. Awaiting it yielded nothing, so
			// the next call signed in with an empty username and token -- a 401 the shared
			// interceptor read as an expired session, dropping the one just minted and reloading
			// the page. A wrong code showed no error either, because nothing ever threw.
			await login(await AuthService.completeTwoFactor({ token: challenge?.token, ...proof }));
		}
		catch(err) {
			// A challenge that has expired, been torn down, or run out of attempts cannot be
			// satisfied by any code. The prompt has no way back of its own, so without this the
			// person was left typing correct codes into a screen that would refuse forever --
			// only a page reload recovered. Back to the password, with the reason attached.
			if( err?.status === 401 || err?.status === 429 ) {
				setChallenge(null);
				setStep(STEP_PASSWORD);
			}

			setStepError(err?.message || 'That code could not be used');
		}
		finally {
			setVerifying(false);
		}
	};

	/**
	 * Forget the sign-in in progress.
	 *
	 * Called when the modal closes. Without it a typed password sat in component state for the
	 * life of the page, and the modal reopened after a logout on whatever screen it had closed on
	 * -- a password field already filled in for the previous person, or a code prompt whose
	 * challenge was long dead. The address is kept: it is not a secret, and somebody signing back
	 * in on their own machine is usually the same somebody.
	 */
	const resetFlow = () => {
		setStep(STEP_IDENTIFIER);
		setStepError(null);
		// The passkey and provider refusals live in the auth hook, not here, and are rendered on
		// every step -- so a refusal for the last address followed the person to the next one.
		clearLoginErrors?.();
		setPassword('');
		setChallenge(null);
		setPasskeyAvailable(false);
		setPasskeyRequired(false);
		setLinkKind('signin');
		setResendIn(0);
	};

	/**
	 * Enter is the button on the password screen too.
	 */
	const handlePasswordEnter = (e) => {
		if( e.key === 'Enter' ) {
			handlePassword(e);
		}
	};

	/**
	 * The address being signed in, with a pencil back to the first screen.
	 */
	const renderIdentity = () => (
		<p className="px-auth-card-identity">
			{email.trim()}
			<button
				type="button"
				className="px-auth-card-edit"
				aria-label="Change email address"
				data-cy="sign-in-change-email"
				// The same reset the modal performs on close, minus the address -- one list to
				// maintain rather than two that had already drifted: this one forgot the
				// challenge, the link kind and the resend countdown.
				onClick={resetFlow}
			>
				<PencilMark/>
			</button>
		</p>
	);

	/**
	 * Whatever went wrong on this screen.
	 *
	 * The passkey mutation reports through `ssoLoginError`, so both are shown here -- a refusal
	 * the API bothered to explain ("no account uses this address", "this account is no longer
	 * active") is the actionable half.
	 */
	const renderStepError = () => {
		const message = stepError || ssoLoginError?.message;

		if( !message ) {
			return null;
		}

		return <div className="px-auth-modal-error" data-cy="sign-in-error">{message}</div>;
	};

	/**
	 * Ask who this is, and nothing else.
	 *
	 * What comes next depends on what the address has, which only the API knows — so this screen
	 * cannot decide anything beyond collecting it.
	 */
	const renderIdentifierStep = () => (
		<form name="signInForm" onSubmit={handleIdentifier}>
			<AuthCard
				title="Sign in to Pixwel Platform"
				subtitle="Welcome back! Please sign in to continue"
			>
				{ renderStepError() }

				<div className="px-auth-card-field">
					<Label htmlFor="signInEmail" className="mb-2 block">Email address</Label>
					<Input
						ref={emailRef}
						id="signInEmail"
						type="email"
						placeholder="Enter your email address"
						value={email}
						onChange={handleEmailChange}
						onKeyDown={handleEnter}
						onAnimationStart={handleAutoFill}
						data-cy="sign-in-email"
						autoComplete="username webauthn"
						autoFocus
					/>
				</div>

				<Button
					type="button"
					size="lg"
					className="w-full"
					disabled={!email.trim() || busy}
					data-cy="sign-in-continue"
					onClick={handleIdentifier}
				>
					Continue
					<PlayMark/>
				</Button>
			</AuthCard>
		</form>
	);

	/**
	 * The passkey, as either the main action or the aside.
	 *
	 * Which one follows the policy. Where a passkey is required it is the only way through, so it
	 * takes the primary button. Where it is merely available, the link leads instead: a passkey
	 * is the better credential but only on a device that holds one, and the screen should lead
	 * with whichever is actually being asked for rather than with the one we would prefer.
	 */
	const renderPasskeyButton = (primary) => (
		<Button
			type="button"
			variant={primary ? 'default' : 'ghost'}
			size={primary ? 'lg' : 'sm'}
			className={primary ? 'w-full' : ALTERNATIVE_BUTTON_CLASS}
			disabled={busy}
			data-cy="sign-in-passkey"
			onClick={handlePasskey}
		>
			{ primary ? 'Continue' : 'Use your passkey instead' }
			{ primary ? <PlayMark/> : null }
		</Button>
	);

	/**
	 * The way out for somebody at a machine their passkey is not on.
	 *
	 * Never rendered where a passkey is required: there the link is precisely the thing being
	 * required against, and the API refuses it anyway.
	 */
	const renderLinkButton = () => (
		<Button
			type="button"
			size="lg"
			className="w-full"
			disabled={busy}
			data-cy="sign-in-use-link"
			onClick={() => sendLink(email.trim())}
		>
			Email me a sign-in link
			<PlayMark/>
		</Button>
	);

	/**
	 * Offer the passkey.
	 *
	 * The ceremony is not started on arrival: browsers only show the system prompt for a gesture,
	 * and a prompt nobody asked for is a prompt people dismiss.
	 *
	 * The fingerprint is dropped when the passkey is not the headline -- a mark that large says
	 * what the screen is for, and the screen is then for the link. `renderLinkSentStep` carries
	 * no mark for the same reason.
	 */
	const renderPasskeyStep = () => (
		<form name="passkeyForm" onSubmit={handlePasskey}>
			<AuthCard
				title={passkeyRequired ? 'Use your passkey' : 'Sign in'}
				subtitle={passkeyRequired
					? (passkeyUnavailable
						? 'This account signs in with its passkey, and a passkey cannot be used '
							+ 'from here.'
						: 'This account signs in with its passkey. Your device may ask for your '
							+ 'fingerprint, face or screen lock.')
					: 'We’ll email you a link to sign in. Your passkey works too, if it is '
						+ 'on this device.'}
				mark={passkeyRequired ? <FingerprintMark/> : null}
			>
				{ renderIdentity() }
				{ renderStepError() }

				{ passkeyUnavailable
					? (
						<p className="px-auth-card-note" data-cy="sign-in-passkey-unavailable">
							Passkeys are not available right now -- this browser or this
							deployment cannot run a passkey sign-in, and this account has no
							other way in. Try another browser or device, or ask an administrator.
						</p>
					)
					: (passkeyRequired
						? renderPasskeyButton(true)
						: (
							<>
								{ renderLinkButton() }
								{ renderPasskeyButton(false) }
							</>
						)) }
			</AuthCard>
		</form>
	);

	/**
	 * The password, for as long as it is allowed.
	 *
	 * Everything else stays reachable underneath it. Somebody who has a passkey should still be
	 * able to use it while this is on, and the link is the way through for anybody whose
	 * password this screen has just refused.
	 */
	const renderPasswordStep = () => (
		<form name="passwordForm" onSubmit={handlePassword}>
			<AuthCard
				title="Enter your password"
				subtitle="Your password still works on this account."
			>
				{ renderIdentity() }
				{ renderStepError() }

				<div className="px-auth-card-field">
					<Label htmlFor="signInPassword" className="mb-2 block">Password</Label>
					<Input
						id="signInPassword"
						type="password"
						placeholder="Enter your password"
						value={password}
						disabled={busy}
						data-cy="sign-in-password"
						autoComplete="current-password"
						onChange={(e) => setPassword(e.target.value)}
						onKeyDown={handlePasswordEnter}
						autoFocus
					/>
				</div>

				<Button
					type="button"
					size="lg"
					className="w-full"
					disabled={busy || !password}
					data-cy="sign-in-password-submit"
					onClick={handlePassword}
				>
					Continue
					<PlayMark/>
				</Button>

				{ passkeyAvailable
					? (
						<Button
							type="button"
							variant="ghost"
							className={ALTERNATIVE_BUTTON_CLASS}
							disabled={busy}
							data-cy="sign-in-passkey"
							onClick={handlePasskey}
						>
							Use your passkey instead
						</Button>
					)
					: null }

				<Button
					type="button"
					variant="ghost"
					className={ALTERNATIVE_BUTTON_CLASS}
					disabled={busy}
					data-cy="sign-in-use-link"
					onClick={() => sendLink(email.trim())}
				>
					Email me a sign-in link instead
				</Button>
			</AuthCard>
		</form>
	);

	/**
	 * The code, when a password sign-in owed one.
	 */
	const renderTwoFactorStep = () => (
		<TwoFactorPrompt
			recoveryCodes={challenge?.recoveryCodes}
			error={stepError}
			busy={busy}
			onSubmit={handleSecondFactor}
		/>
	);

	/**
	 * Say that a link is on its way.
	 *
	 * Shown whether or not the address belongs to anybody, because the API answers the same
	 * either way — this screen is the other half of not being an account-enumeration oracle.
	 */
	const renderLinkSentStep = () => (
		<AuthCard
			title="Check your email"
			subtitle={linkKind === 'twofactor'
				? 'Your password was accepted. Use the link sent to your email to finish '
					+ 'signing in.'
				: 'Use the verification link sent to your email'}
		>
			{ renderIdentity() }
			{ renderStepError() }

			{ /*
				No resend for the second-factor mail. `sendLink()` asks for a passwordless
				*sign-in* link, which is a different credential from the `twofactor_login`
				message that actually went out -- so offering "Resend" here would quietly hand
				somebody a way round the second factor they are in the middle of proving, and
				do it without the countdown that throttles the other one.
			*/ }
			{ linkKind === 'twofactor'
				? null
				: (
					<p className="px-auth-card-resend">
						Didn&apos;t receive a link?
						{' '}
						<button
							type="button"
							disabled={resendIn > 0 || busy}
							data-cy="sign-in-resend"
							onClick={() => sendLink(email.trim())}
						>
							Resend{resendIn > 0 ? ` (${resendIn})` : ''}
						</button>
					</p>
				) }
		</AuthCard>
	);

	const renderForm = () => {
		if( step === STEP_PASSKEY ) {
			return renderPasskeyStep();
		}

		if( step === STEP_PASSWORD ) {
			return renderPasswordStep();
		}

		if( step === STEP_TWO_FACTOR ) {
			return renderTwoFactorStep();
		}

		if( step === STEP_LINK_SENT ) {
			return renderLinkSentStep();
		}

		return renderIdentifierStep();
	};

	return (
		<div>
			<ReactModal
				isOpen={shouldBeOpen()}
				onAfterClose={resetFlow}
				contentLabel="Pixwel Sign In"
				className="px-auth-modal"
				overlayClassName="px-auth-overlay"
				bodyOpenClassName="px-body-auth-modal-open"
				htmlOpenClassName="px-html-auth-modal-open"
				// The overlay is the only element that spans the full viewport, which is what
				// the canvas needs behind the card -- `children` only reaches the content div,
				// so this is the one seam react-modal offers for a sibling that large.
				overlayElement={(overlayProps, contentElement) => (
					<div {...overlayProps}>
						<SpotlightBackground themeKey={spotlightThemeKey} />
						{ contentElement }
					</div>
				)}
			>
				{ renderForm() }
			</ReactModal>
		</div>
	);
}

export default AuthModal;
